/*
 * Authors: mariushirjanu
 * Created: 11/08/2021
 */
import _ from 'underscore';
import moment from 'moment';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { IInvoiceTemplateDetail, InvoiceTemplatesDataService, IStoredInvoiceTemplateDetail } from './invoice-templates-data.service';
import { util } from '../../util';
import { FormattingService } from '../../services/formatting.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../formly/ga-formly.service';
import { ICompanyCountry, ICompanyCountryExtended, ICurrency, ILocationExtended } from '@type_definitions/datatypes';
import { InvoiceTemplatesFormattingService } from './invoice-templates-formatting.service';
import { LOV_ACSL_COMCO, LOV_ACSL_CURRENCY, LOV_CURRENCY_SOURCE, LOV_PAYMENT_FREQUENCY, LOV_PAYMENT_TYPE, LovService } from '../../services/lov.service';
import { GaMessageService, IDialog } from '../../message/ga-message.service';
import { GaValidateService } from '../../formly/ga-validate.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaEventService, IGAEvent } from '../../services/ga-event.service';
import { Subscription } from 'rxjs';
import { EVENT_CANCEL_BP_INVOICE_TEMPLATE, EVENT_EDIT_BP_INVOICE_TEMPLATE, EVENT_SAVE_BP_INVOICE_TEMPLATE, InvoiceTemplatesService } from './invoice-templates.service';
import { GaWizard } from '../../wizard/ga-wizard';
import { GaWizardService } from '../../wizard/ga-wizard.service';
import { GaDeferredService, IGADeferred } from '../../services/ga-deferred.service';
import { WIZARD_COMPANY_PROFILES } from '../../app-wizards';
import { GaStateService } from '../../services/ga-state.service';
import { TranslationService } from '../../translation/translation.service';

const ACTION_EDIT:string = 'EDIT';
const ACTION_SAVE:string = 'SAVE';
const ACTION_CANCEL:string = 'CANCEL';
const OUTGOING_INVOICE_FOR_ORDER_FEES = 'Outgoing invoice for order fees';

@Component({
  selector: 'invoice-template-detail',
  template: `
    <div class="back-to-overview">
      <a class="btn btn-primary" [uiSref]="overviewState"><i class="fa  fa-chevron-left"></i>&nbsp;<span translate>INVOICE.INVOICE_TEMPLATES.BACK_TO_OVERVIEW</span></a>
    </div>
    <form>
      <div class="form-horizontal">
        <h2>{{model?.type}}</h2>
        <div *ngFor="let fields of formSections">
          <formly [form]="form" [fields]="fields" [model]="model"></formly>
        </div>
      </div>
    </form>
  `
})
export class InvoiceTemplateDetailComponent implements OnInit, OnDestroy {
  @Input() public companyCountryId:number;
  @Input() public invoiceTemplateId:number;
  @Input() public overviewState:string;
  @Output() public onSaved:EventEmitter<IInvoiceTemplateDetail | IStoredInvoiceTemplateDetail> = new EventEmitter<IInvoiceTemplateDetail | IStoredInvoiceTemplateDetail>();
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();

  public model:IInvoiceTemplateDetail;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public formSections:IExtraFieldConfigurationObject[][];

  private backupModel:IInvoiceTemplateDetail;
  private backupCompanyCountryModel:IInvoiceTemplateDetail;
  public diffCompanyCountry:boolean; // flag which indicates if it's selected a different company country than the BPProfile company country
  private locationCCId:number;
  private acslLocationCCId:number;
  private viewSpecial:boolean;
  private type:string;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private valueClass:string = 'col-sm-9';
  private labelClass:string = 'col-sm-3';
  private acslCompanyCountryField:IExtraFieldConfigurationObject;
  private acslLocationField:IExtraFieldConfigurationObject;
  private bankCostField:IExtraFieldConfigurationObject;
  private companyCountrySelectField:IExtraFieldConfigurationObject;
  private contactField:IExtraFieldConfigurationObject;
  private currencyField:IExtraFieldConfigurationObject;
  private paymentTypeField:IExtraFieldConfigurationObject;
  private paymentFrequencyField:IExtraFieldConfigurationObject;
  private locationField:IExtraFieldConfigurationObject;
  private minAmountField:IExtraFieldConfigurationObject;

  private readOnlyExp = ():boolean => this.editing && !this.diffCompanyCountry;

  constructor(
    private authService:AuthService,
    private deferredService:GaDeferredService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private gaEventService:GaEventService,
    private invoiceTemplatesService:InvoiceTemplatesService,
    private invoiceTemplatesFormattingService:InvoiceTemplatesFormattingService,
    private gaMessageService:GaMessageService,
    private invoiceTemplatesDataService:InvoiceTemplatesDataService,
    private lovService:LovService,
    private gaValidateService:GaValidateService,
    private wizardService:GaWizardService,
    private gaStateService:GaStateService,
    private translationService:TranslationService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.wizard = this.wizardService.getInstance(WIZARD_COMPANY_PROFILES);
    this.start();
  }

  public start():void {
    // specify translation screen for this component
    this.gaStateService.screen = 'INVOICE.INVOICE_TEMPLATES';
    this.viewSpecial = this.authService.checkAcslUser();
    this.invoiceTemplatesService.form = this.form;
    this.wizard.actions([
      this.wizardService.createActionButton(ACTION_EDIT, this.translationService.instant('GA.BUTTONS.EDIT'), 'btn-primary', this.edit),
      this.wizardService.createActionButton(ACTION_SAVE, this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'INVOICE.INVOICE_TEMPLATES.TITLE' }), 'btn-success', this.save, true),
      this.wizardService.createActionButton(ACTION_CANCEL, this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.SAVE' }), 'btn-danger', this.cancelSave, true)
    ]);

    this.initSections();
    // invoiceTemplateDetail is called in order to get the invoice template's direction
    this.invoiceTemplatesDataService.invoiceTemplateDetail(this.invoiceTemplateId)
      .then((invoiceTemplateDetail:IInvoiceTemplateDetail) => {
        this.type = invoiceTemplateDetail.type;
        // load the used invoice template to populate read-only fields
        this.invoiceTemplatesDataService.usedInvoiceTemplateDetail(this.companyCountryId, invoiceTemplateDetail.direction)
          .then((invoiceTemplateReal:IInvoiceTemplateDetail) => {
            this.setDiffCompanyCountry(invoiceTemplateReal.companyCountryId);

            // select the invoice template based on direction
            this.model = this.invoiceFromGet(invoiceTemplateReal);
            this.editing = this.invoiceTemplatesService.editing;
            this.setCurrency(this.model.currency);
            this.listen();
          });

      });
  }

  private listen():void {
    // events needed when this component is loaded inside of bpprofiles
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      switch (event.name) {
        case EVENT_SAVE_BP_INVOICE_TEMPLATE:
          this.save();
          break;

        case EVENT_CANCEL_BP_INVOICE_TEMPLATE:
          this.cancelSave();
          break;

        case EVENT_EDIT_BP_INVOICE_TEMPLATE:
          this.editing = event.data;
          break;
      }
    });
  }

  public get editing():boolean {
    return this.invoiceTemplatesService.editing;
  }

  public set editing(editing:boolean) {
    this.invoiceTemplatesService.editing = editing;
    this.setButtonsEditing(editing);
    if (editing) {
      this.backupModel = util.deepClone(this.model);
      this.fillFields();
      this.onCompanyCountryChange(this.model.companyCountry);
    }
  }

  private initSections():void {
    this.formSections = [
      this.createInvoiceAddressFields(),
      this.createAcslInvoiceAddressFields(),
      this.createInvoiceSettingsFields()
    ];
  }

  private createInvoiceAddressFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];

    const ccWarn:string = `
      <div class="alert alert-warning company-country-warning">
        <i class="fa fa-warning"></i>
        ${this.translationService.instant('INVOICE.INVOICE_TEMPLATES.CC_DIFFERENT_WARNING')}
      </div>`;

    fields.push(
      this.formlyService.aTitle('INVOICE.INVOICE_TEMPLATES.MYINVOICEADDRESS.TITLE').build(),

      this.formlyService.aValue('companyCountry', 'GA.GENERAL.COMPANY')
        .withoutEditable()
        .withHideExpression(() => this.editing)
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      // this field is still visible when selected company country is different from the BPProfiles company country
      this.companyCountrySelectField = this.formlyService.aSelect('companyCountry', 'GA.GENERAL.COMPANY')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !this.editing)
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .onChange(this.onCompanyCountryChange)
        .build(),

      this.formlyService.aTemplate(ccWarn)
        .withHideExpression(() => !(this.editing && this.diffCompanyCountry))
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('location', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.invoiceTemplatesFormattingService.formatLocationL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.locationField = this.formlyService.aSelect('location', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.invoiceTemplatesFormattingService.formatLocationS)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),
      this.formlyService.aValue('location', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.invoiceTemplatesFormattingService.formatLocation)
        .build(),


      this.formlyService.aValue('contact', 'GA.GENERAL.CONTACT')
        .withoutEditable()
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatContactInfo)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.contactField = this.formlyService.aSearch('contact', 'GA.GENERAL.CONTACT')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.formattingService.formatContactInfo)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withExtraOptions({
          templateOptions: {
            minSearchLength: 1
          }
        })
        .build(),

      this.formlyService.aValue('extraReceiverEmailAddresses')
        .withHideExpression(this.readOnlyExp)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withoutInfo()
        .build(),

      this.formlyService.aTextarea('extraReceiverEmailAddresses')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .showInfo()
        .build()
    );

    return fields;
  }

  private createAcslInvoiceAddressFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];

    fields.push(
      this.formlyService.aTitle('INVOICE.INVOICE_TEMPLATES.ACSLINVOICEADDRESS.TITLE').build(),

      this.formlyService.aValue('acslCompanyCountry', 'GA.GENERAL.COMPANY')
        .withoutEditable()
        .withHideExpression(() => this.readOnlyExp() && this.viewSpecial)
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.acslCompanyCountryField = this.formlyService.aSelect('acslCompanyCountry', 'GA.GENERAL.COMPANY')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !(this.readOnlyExp() && this.viewSpecial))
        .withFormat(this.formattingService.formatCompanyCountryL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .onChange(this.onAcslCompanyCountryChange)
        .build(),

      this.formlyService.aValue('acslLocation', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .withHideExpression(() => this.readOnlyExp() && this.viewSpecial)
        .withFormat(this.invoiceTemplatesFormattingService.formatLocationL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.acslLocationField = this.formlyService.aSelect('acslLocation', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !(this.readOnlyExp() && this.viewSpecial))
        .withFormat(this.invoiceTemplatesFormattingService.formatLocationS)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),
      this.formlyService.aValue('acslLocation', 'GA.GENERAL.LOCATION')
        .withoutEditable()
        .withHideExpression(() => !(this.readOnlyExp() && this.viewSpecial))
        .withFormat(this.invoiceTemplatesFormattingService.formatLocation)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('acslContact', 'GA.GENERAL.CONTACT')
        .withoutEditable()
        .withFormat(this.formattingService.formatContact)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build()
    );

    return fields;
  }

  private createInvoiceSettingsFields():IExtraFieldConfigurationObject[] {
    const fields:IExtraFieldConfigurationObject[] = [];

    fields.push(
      this.formlyService.aTitle('INVOICE.INVOICE_TEMPLATES.INVOICESETTINGS.TITLE').build(),

      this.formlyService.aValue('currency', 'GA.GENERAL.CURRENCY')
        .withoutEditable()
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatCurrencyL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.currencyField = this.formlyService.aSelect('currency', 'GA.GENERAL.CURRENCY')
        .withoutEditable()
        .isRequired()
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.formattingService.formatCurrencyL)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .onChange(this.setCurrency)
        .build(),

      this.formlyService.aValue('subject')
        .withHideExpression(this.readOnlyExp)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.anInput('subject')
        .withHideExpression(() => !this.readOnlyExp())
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('paymentTerms')
        .withHideExpression(() => this.readOnlyExp() && this.viewSpecial)
        .withFormat(this.formattingService.formatDays)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.anInput('paymentTerms')
        .isRequired()
        .withHideExpression(() => !(this.readOnlyExp() && this.viewSpecial))
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withAddOn('Days')
        .withExtraOptions({
          templateOptions: {
            max: 999
          },
          data: {
            inputType: 'positiveInteger'
          }
        })
        .build(),

      this.formlyService.aValue('minimumAmount')
        .withHideExpression(() => this.readOnlyExp() && this.viewSpecial)
        .withFormat(this.formatAmount)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.minAmountField = this.formlyService.anInput('minimumAmount')
        .withHideExpression(() => !(this.readOnlyExp() && this.viewSpecial))
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withAddOn(' ') //make sure the addon is being added, and set it later
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            max: 999999999
          },
          data: {
            inputType: 'positiveReal'
          }
        })
        .build(),

      this.formlyService.aValue('bankCostPct')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.invoiceTemplatesFormattingService.formatPct)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.formlyService.anInput('bankCostPct')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withAddOn('%')
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            max: 999
          },
          data: {
            inputType: 'positiveReal'
          }
        })
        .build(),

      this.formlyService.aValue('bankCostFixed')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formatAmount)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.bankCostField = this.formlyService.anInput('bankCostFixed')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withAddOn(' ') //make sure the addon is being added, and set it later
        .showInfo()
        .withExtraOptions({
          templateOptions: {
            max: 9999999
          },
          data: {
            inputType: 'positiveReal'
          }
        })
        .build(),

      this.formlyService.aValue('paymentFrequency')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.paymentFrequencyField = this.formlyService.aSelect('paymentFrequency')
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .showInfo()
        .build(),

      this.formlyService.aValue('paymentStartDate')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.formlyService.aDateInput('paymentStartDate')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .showInfo()
        .build(),

      this.formlyService.aValue('paymentEndDate')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.formlyService.aDateInput('paymentEndDate')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .showInfo()
        .build(),

      this.formlyService.aValue('paymentType')
        .withHideExpression(this.readOnlyExp)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.paymentTypeField = this.formlyService.aSelect('paymentType')
        .withHideExpression(() => !this.readOnlyExp())
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .build(),

      this.formlyService.aValue('paymentAmount')
        .withHideExpression(this.readOnlyExp)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.formlyService.anInput('paymentAmount')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .showInfo()
        .withExtraOptions({
          data: {
            inputType: 'positiveReal'
          }
        })
        .build(),

      this.formlyService.aValue('bankAccount_bankId')
        .withHideExpression(this.readOnlyExp)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aSelect('bankAccount_bankId')
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withOptions([
          'LOC',
          'USD',
          'EUR'
        ])
        .build(),

      this.formlyService.aValue('comments', 'INVOICE.INVOICE_TEMPLATES.INVOICEINSTRUCTIONS.LABEL')
        .withoutEditable()
        .withHideExpression(this.readOnlyExp)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aTextarea('comments', 'INVOICE.INVOICE_TEMPLATES.INVOICEINSTRUCTIONS.LABEL')
        .withoutEditable()
        .withHideExpression(() => !this.readOnlyExp())
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .build(),

      this.formlyService.aValue('paymentInstructions', 'INVOICE.INVOICE_TEMPLATES.PAYMENTINSTRUCTIONS.LABEL')
        .withoutEditable()
        .withHideExpression(() => this.type === OUTGOING_INVOICE_FOR_ORDER_FEES ? this.readOnlyExp() : true)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aTextarea('paymentInstructions', 'INVOICE.INVOICE_TEMPLATES.PAYMENTINSTRUCTIONS.LABEL')
        .withoutEditable()
        .withHideExpression(() => this.type === OUTGOING_INVOICE_FOR_ORDER_FEES ? !this.readOnlyExp() : true)
        .withLabelClass(this.labelClass)
        .withValueClass('col-sm-9')
        .build(),

      this.formlyService.aValue('withholdingTaxPct', 'INVOICE.INVOICE_TEMPLATES.WHT.LABEL')
        .withHideExpression(() => this.type === OUTGOING_INVOICE_FOR_ORDER_FEES ? this.readOnlyExp() : true)
        .withFormat(this.invoiceTemplatesFormattingService.formatPct)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .showInfo()
        .build(),

      this.formlyService.anInput('withholdingTaxPct', 'INVOICE.INVOICE_TEMPLATES.WHT.LABEL')
        .withHideExpression(() => this.type === OUTGOING_INVOICE_FOR_ORDER_FEES ? !this.readOnlyExp() : true)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withInfoClass('INVOICE.INVOICE_TEMPLATES.WHT.INFO')
        .showInfo()
        .withAddOn('%')
        .withExtraOptions({
          templateOptions: {
            max: 99
          },
          data: {
            inputType: 'positiveReal'
          }
        })
        .build()
    );
    return fields;
  }

  private edit = ():void => {
    this.editing = true;
  };

  public save = ():void => {
    this.wizard.setActionBusy(ACTION_SAVE, true);
    this.wizard.setActionEnabled(ACTION_CANCEL, false);

    const doSave:Function = ():Promise<string | IStoredInvoiceTemplateDetail> => {
      const saveDeferred:IGADeferred<string|IStoredInvoiceTemplateDetail> = this.deferredService.defer();
      const invoiceData = this.invoiceForPost();

      if (!this.diffCompanyCountry) {
        // SAVE ALL DATA
        let errors:string[] = [];
        const promises:Promise<void|string[]>[] = [];
        _.each(this.formSections, (formSection:IExtraFieldConfigurationObject[]) => {
          promises.push(
            this.gaValidateService.validateFields(formSection, this.model, true)
          );
        });

        Promise.all(promises)
          .then(() => {
             this.invoiceTemplatesDataService.saveInvoiceTemplateDetail(this.invoiceTemplateId, invoiceData)
              .then((invoiceTemplateDetail:IInvoiceTemplateDetail) => {
                this.backupModel = null;
                this.invoiceTemplatesService.editing = false;
                saveDeferred.resolve(invoiceTemplateDetail);
              })
               .catch(saveDeferred.reject);
          })
          .catch((fieldErrors:string) => {
            errors = errors.concat(fieldErrors);
            const invalidFormDialog:IDialog = this.gaMessageService.showInvalidFormMessage('Validation failed', undefined, errors, () => {
              invalidFormDialog.hide();
            });
            saveDeferred.reject('Validation failed');
          });
      } else {
        // UPDATE COMPANY COUNTRY ID
        if (invoiceData?.companyCountry?.id) {
          const dataToUpdate = {
            id: this.invoiceTemplateId,
            companyCountry: {
              id: invoiceData.companyCountry.id
            }
          };

          this.invoiceTemplatesDataService.updateInvoiceTemplateDetail(this.invoiceTemplateId, <IStoredInvoiceTemplateDetail>dataToUpdate)
            .then((invoiceTemplateDetail:IInvoiceTemplateDetail) => {
              this.backupModel = null;
              this.invoiceTemplatesService.editing = false;
              saveDeferred.resolve(invoiceTemplateDetail);
            })
            .catch(saveDeferred.reject);
        } else {
          saveDeferred.reject('Cannot save');
        }
      }

      return saveDeferred.promise;
    };


    doSave()
      .then((invoiceTemplateDetail:IInvoiceTemplateDetail) => {
        this.editing = false;
        this.onSaved.emit(invoiceTemplateDetail);
      })
      .catch(() => {
        this.onCancel.emit();
      })
      .finally(() => {
        this.wizard.setActionBusy(ACTION_SAVE, false);
        this.wizard.setActionEnabled(ACTION_CANCEL, true);
      });
  };

  private cancelSave = ():void => {
    this.form.patchValue(util.deepClone(this.backupModel));
    this.backupModel = null;
    this.backupCompanyCountryModel = null;
    this.invoiceTemplatesService.editing = false;
    this.editing = false;
  };

  private formatAmount = (value:number | string):string => {
    return this.formattingService.formatAmount(value, this.model?.currency);
  };

  private fillFields():void {
    this.formlyService.fillField(this.companyCountrySelectField, this.invoiceTemplatesDataService.companyCountries(this.companyCountryId, this.model.direction));
    this.formlyService.fillField(this.currencyField, this.lovService.list(LOV_ACSL_CURRENCY));
    this.formlyService.fillField(this.paymentTypeField, this.lovService.list(LOV_PAYMENT_TYPE));
    this.formlyService.fillField(this.paymentFrequencyField, this.lovService.list(LOV_PAYMENT_FREQUENCY));

    if (this.viewSpecial) {
      this.formlyService.fillField(this.acslCompanyCountryField, this.lovService.list(LOV_ACSL_COMCO));
      if (this.model?.acslCompanyCountry?.id) {
        this.formlyService.fillField(this.acslLocationField, this.invoiceTemplatesDataService.acslLocations(this.model.acslCompanyCountry.id));
      }
    }
  }

  private setButtonsEditing(editing:boolean):void {
    this.wizard.setActionVisible(ACTION_EDIT, !editing);
    this.wizard.setActionVisible(ACTION_SAVE, editing);
    this.wizard.setActionVisible(ACTION_CANCEL, editing);
  }

  private onCompanyCountryChange = (companyCountry:ICompanyCountry, oldCompanyCountry?:ICompanyCountryExtended):void => {
    const companyCountryId:number = companyCountry?.id;
    if (this.editing && companyCountryId && companyCountryId !== this.locationCCId) {
      this.companyCountryChanged(companyCountry)
        .then(() => {
          this.formlyService.fillField(this.locationField, this.invoiceTemplatesDataService.locations(companyCountryId)
            .then((locations:ILocationExtended[]) => {
              if (oldCompanyCountry?.id && companyCountryId !== oldCompanyCountry?.id && !this.diffCompanyCountry) { //real change using the same companyCountry
                this.form.patchValue({ location: undefined });
              }
              this.locationCCId = companyCountryId;
              return locations;
            }));
        });
    }

    this.contactField.templateOptions.url = `rest/invoicetemplate/invoicecontacts?companyCountryId=${companyCountryId}`;
  };

  // This function is called when a company country is selected, it checks if company country is different than the BPProfile company country and do the following:
  // - if a different company country is selected, it fetches a new invoice template data of the selected company country and sets it to model
  // - if the same company country is selected, it restores the data of the saved company country and sets it to model
  private companyCountryChanged = (selectedCompanyCountry:ICompanyCountryExtended):Promise<void | IInvoiceTemplateDetail> => {
    const selectedCompanyCountryId:number = selectedCompanyCountry?.id;
    this.setDiffCompanyCountry(selectedCompanyCountryId);

    this.backupCompanyCountryModel = _.clone(this.backupModel);
    if (util.isUndefined(this.backupCompanyCountryModel)) {
      return Promise.reject();
    }

    if (this.backupCompanyCountryModel?.companyCountry && this.backupCompanyCountryModel.companyCountry.id !== selectedCompanyCountryId) {
      const selectedCompanyCountryObj = this.model.companyCountry;
      this.model = <any>{
        companyCountry: selectedCompanyCountryObj
      };

      this.setCCFieldLoadingDisabled(true);

      if (selectedCompanyCountry.invoiceTemplate?.id) {
        return this.invoiceTemplatesDataService.invoiceTemplateDetail(selectedCompanyCountry.invoiceTemplate.id)
          .then((data:IInvoiceTemplateDetail) => {
            // overwrite companycountry fetched with the selected one
            if (selectedCompanyCountryObj) {
              data.companyCountry = selectedCompanyCountryObj;
            }
            this.model = this.invoiceFromGet(data);
          })
          .finally(() => {
            this.setCCFieldLoadingDisabled(false);
          });
      } else {
        this.setCCFieldLoadingDisabled(false);
        return Promise.resolve();
      }

    } else {
      this.model = this.backupCompanyCountryModel;
      return Promise.resolve();
    }
  };

  private onAcslCompanyCountryChange = (companyCountry?:ICompanyCountryExtended, oldCompanyCountry?:ICompanyCountryExtended):void => {
    const companyCountryId:number = companyCountry?.id;
    if (this.editing && companyCountryId && companyCountryId !== this.acslLocationCCId) {
      this.formlyService.fillField(this.acslLocationField, this.invoiceTemplatesDataService.acslLocations(companyCountryId))
        .then(() => {
          this.acslLocationCCId = companyCountryId;
          if (oldCompanyCountry?.id && companyCountryId !== oldCompanyCountry?.id) { //real change
            // do not add the initial option anymore
            this.acslLocationField.templateOptions.addInitialOption = false;
            this.model.acslLocation = undefined;
          }
        });
    }
  };

  private invoiceFromGet(data:IInvoiceTemplateDetail):IInvoiceTemplateDetail {
    if (data.bankAccount) {
      util.setValue(data, 'bankAccount_bankId', data.bankAccount.bankId, true);
      util.setValue(data, 'bankAccount_bankName', data.bankAccount.bankName, true);
      util.setValue(data, 'bankAccount_accountNumber', data.bankAccount.accountNumber, true);
      util.setValue(data, 'bankAccount_country', data.bankAccount.country, true);
      data.bankAccount = undefined;
    }

    return data;
  }

  private invoiceForPost():any {
    const detail:any = util.deepClone(this.model);
    if (detail.paymentStartDate) {
      detail.paymentStartDate = parseInt(moment(detail.paymentStartDate).format('x'));
    }
    if (detail.paymentEndDate) {
      detail.paymentEndDate = parseInt(moment(detail.paymentEndDate).format('x'));
    }
    if (detail.bankAccount_bankName || detail.bankAccount_accountNumber || detail.bankAccount_country || detail.bankAccount_bankId) {
      detail.bankAccount = {};
      util.setValue(detail.bankAccount, 'bankName', detail.bankAccount_bankName, true);
      util.setValue(detail.bankAccount, 'accountNumber', detail.bankAccount_accountNumber, true);
      util.setValue(detail.bankAccount, 'country', detail.bankAccount_country, true);
      util.setValue(detail.bankAccount, 'bankId', detail.bankAccount_bankId, true);

      detail.bankAccount_bankName = undefined;
      detail.bankAccount_accountNumber = undefined;
      detail.bankAccount_country = undefined;
      detail.bankAccount_bankId = undefined;
    }
    return detail;
  }

  private setDiffCompanyCountry(selectedCompanyCountryId:number):void {
    this.diffCompanyCountry = selectedCompanyCountryId !== this.companyCountryId;
  }

  private setCCFieldLoadingDisabled(loadingAndDisabled:boolean):void {
    this.companyCountrySelectField.templateOptions.loading = loadingAndDisabled;
    this.companyCountrySelectField.templateOptions.disabled = loadingAndDisabled;
  }

  private setCurrency = (currency:ICurrency):void => {
    const formatted:string = this.formattingService.formatCurrencyS(currency) || ' ';
    if (this.minAmountField) {
      this.minAmountField.templateOptions.addOn = formatted;
    }
    this.bankCostField.templateOptions.addOn = formatted;
  };

  public ngOnDestroy():void {
    this.wizard.clear();
    this.invoiceTemplatesDataService.clear();
    this.eventHandler?.unsubscribe();
    this.invoiceTemplatesService.editing = false;
  }
}